import React, {
    ChangeEvent,
    ReactText,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { connect as connectR } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router';
import LeftSide from '../main/left-side';
import LargeCard from '../main/large-card';
import Navbar from '../navigation/navbar';
import Postit from '../main/postit';
import TitleCard from '../main/title-card';
import { ResetPartnerContacts } from '../../store/partnerContactsSlice';
import { INTERNAL_PARTNER_MANAGER } from '../../util/common';
import {
    LoadPartnerByLocationId,
    LoadPartnerManagerDetails,
    LoadPartners,
    ManagedPartnerDto,
    ManagedPartnerWithLocationDto,
    PartnerManagerDetails,
    SelectPartnerById,
} from '../../store/partnerManagerSlice';
import { REQUEST_SUCCESS } from '../../store/network/managedRequest';
import {
    goToCreatePartner,
    goToDashboard,
    goToLocation,
} from '../../nav/navigationService';
import { RootState } from '../../store';
import useTranslate from '../useTranslate';
import { PbGenericRequest } from '../../store/pbRequestSlice';
import { PbRequest } from '../../store/pbRequest';

interface Props {
    partnerManagerDetails: PartnerManagerDetails | null;
    partners: ManagedPartnerDto[] | undefined;
    canCreateAccountingArea: boolean;
    partnerByLocationId: PbGenericRequest;
    partnerByLocationIdData: ManagedPartnerWithLocationDto | undefined;

    ResetPartnerContacts: () => void;
    LoadPartners: () => void;
    LoadPartnerManagerDetails: () => void;
    LoadPartnerByLocationId: (locationId: ReactText) => void;
    SelectPartnerById: (partnerId: ReactText) => Promise<void>;
}

function PartnersOverview(props: Readonly<Props>) {
    const strings = useTranslate('PartnersOverview');

    useEffect(() => {
        props.LoadPartners();
        props.LoadPartnerManagerDetails();
        props.ResetPartnerContacts();
    }, []);

    function _handlePartnerSelection(
        event: unknown,
        externalId: ReactText
    ): Promise<void> {
        return props.SelectPartnerById(externalId);
    }

    function _handleNewPartnerClick() {
        goToCreatePartner();
    }

    const [searchString, setSearchString] = useState<string>('');

    function filter(filterString: string) {
        setSearchString(filterString);
    }

    const partners = useMemo<ManagedPartnerDto[]>(() => {
        if (!props.partners) {
            return [];
        }
        if (!searchString) {
            return props.partners;
        }

        return props.partners.filter(
            (partner) =>
                (partner.id &&
                    String(partner.id)
                        .toLowerCase()
                        .includes(searchString.toLowerCase())) ||
                (partner.customer_id &&
                    String(partner.customer_id).includes(searchString)) ||
                partner.street_and_housenumber
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                partner.postalcode
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                partner.city
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                partner.company_name
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) ||
                partner.credit_note_recipient
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase())
        );
    }, [props.partners, searchString]);

    function searchByLocationId(locationId: string) {
        if (locationId && Number(locationId) > 1) {
            props.LoadPartnerByLocationId(locationId);
        }
    }

    function renderPartnerForLocation(
        partner: ManagedPartnerWithLocationDto,
        showPartnerId: boolean
    ) {
        const handleSuccess = () => {
            if (partner.mansion_id && partner.location_id) {
                goToLocation(partner.mansion_id, partner.location_id);
            } else {
                goToDashboard();
            }
        };
        return renderPartnerForClick(partner, showPartnerId, handleSuccess);
    }

    function renderPartner(partner: ManagedPartnerDto, showPartnerId: boolean) {
        const handleSuccess = () => {
            goToDashboard();
        };
        return renderPartnerForClick(partner, showPartnerId, handleSuccess);
    }

    function renderPartnerForClick(
        partner: ManagedPartnerDto,
        showPartnerId: boolean,
        handleSuccess: () => void
    ) {
        const handleClick = (event: unknown) => {
            _handlePartnerSelection(event, partner.external_id).then(
                handleSuccess
            );
        };

        return (
            <tr
                key={partner.external_id}
                data-testid={`partner-row-${partner.external_id}`}
            >
                {showPartnerId && (
                    <td>
                        {partner.id}
                        <br />
                        <span style={{ opacity: 0.8 }}>
                            {partner.customer_id}
                        </span>
                    </td>
                )}
                <td>{partner.name}</td>
                <td>{partner.credit_note_recipient}</td>
                <td>{partner.company_name}</td>
                <td>
                    {partner.street_and_housenumber}
                    <br />
                    {partner.postalcode} {partner.city}
                </td>
                <td>
                    <Link
                        to={'/'}
                        className="icon icon-wew-button-enter icon-sm"
                        onClick={handleClick}
                    />
                </td>
            </tr>
        );
    }

    const partnerByLocation = props.partnerByLocationIdData;
    const partnerByLocationReady =
        props.partnerByLocationId.status === REQUEST_SUCCESS;

    const partnerManagerDetails = props.partnerManagerDetails;

    const showPartnerId = partnerManagerDetails
        ? INTERNAL_PARTNER_MANAGER.includes(partnerManagerDetails.type)
        : false;

    return (
        <Navbar nav>
            <Row>
                <LeftSide>
                    <TitleCard
                        title={[
                            strings.titleCardTitle,
                            strings.titleCardSubtitle,
                        ]}
                    />
                    <Postit>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: strings.postitText,
                            }}
                        />
                    </Postit>
                </LeftSide>

                <Col xs={9} className={'col-middle'}>
                    {showPartnerId && (
                        <LargeCard
                            type={'card-edit'}
                            title={strings.titlePartnerByLocation}
                        >
                            <input
                                type="number"
                                id="searchByLocationId"
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>
                                ) => searchByLocationId(event.target.value)}
                                placeholder={strings.searchByLocationId}
                                className="bigInputForSearchingOrFiltering"
                            />
                            {partnerByLocation && partnerByLocationReady && (
                                <table
                                    className="data-table"
                                    style={{ width: '100%' }}
                                    id="managedPartnerByLocationIdTable"
                                >
                                    <thead>
                                        <tr>
                                            {showPartnerId && (
                                                <th>
                                                    {strings.titlePartnerId}
                                                </th>
                                            )}
                                            <th>{strings.titleContact}</th>
                                            <th>
                                                {
                                                    strings.titleCreditNoteRecipient
                                                }
                                            </th>
                                            <th>{strings.titleCompany}</th>
                                            <th>{strings.titleAddress}</th>
                                            <th>{strings.titleButton}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderPartnerForLocation(
                                            partnerByLocation,
                                            showPartnerId
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </LargeCard>
                    )}
                    <LargeCard type={'card-edit'} title={strings.title}>
                        {props.canCreateAccountingArea && (
                            <div className={'mb-10'}>
                                <Button
                                    onClick={() => _handleNewPartnerClick()}
                                    className="btn btn-primary btn-add"
                                    style={{ color: 'white' }}
                                >
                                    {strings.newPartner}
                                </Button>
                            </div>
                        )}
                        <input
                            type="text"
                            id="partnerFilterHint"
                            onChange={(event) => filter(event.target.value)}
                            placeholder={
                                showPartnerId
                                    ? strings.partnerFilterHintFS
                                    : strings.partnerFilterHint
                            }
                            className="bigInputForSearchingOrFiltering"
                        />
                        <table
                            className="data-table"
                            style={{ width: '100%' }}
                            id="managedPartnerTable"
                            data-testid="partners-table"
                        >
                            <thead>
                                <tr>
                                    {showPartnerId && (
                                        <th>{strings.titlePartnerId}</th>
                                    )}
                                    <th>{strings.titleContact}</th>
                                    <th>{strings.titleCreditNoteRecipient}</th>
                                    <th>{strings.titleCompany}</th>
                                    <th>{strings.titleAddress}</th>
                                    <th>{strings.titleButton}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partners.map((partner) =>
                                    renderPartner(partner, showPartnerId)
                                )}
                            </tbody>
                        </table>
                    </LargeCard>
                </Col>
            </Row>
        </Navbar>
    );
}

export default connectR(
    (store: RootState) => ({
        partnerManagerDetails: store.partnerManager.partnerManagerDetails,
        canCreateAccountingArea: store.partnerManager.canCreateAccountingArea,
        partners: store.partnerManager.getPartnersResult?.partners || [],
        partnerByLocationId:
            store.pbRequest[PbRequest.GET_PARTNER_BY_LOCATION_ID],
        partnerByLocationIdData: store.partnerManager.partnerByLocationId,
    }),
    {
        ResetPartnerContacts,
        LoadPartners,
        LoadPartnerManagerDetails,
        LoadPartnerByLocationId,
        SelectPartnerById,
    }
)(PartnersOverview);
